import React, { useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { recurrenceOptions } from '../../constants/recurentTypes';

import { useAssignableMembers } from 'lane-shared/domains/workOrder/hooks/useAssignableMembers';
import { convertToUUID } from 'uuid-encoding';
import { DEFAULT_TZ } from 'constants-activate';

import { Dropdown, Flex, Label, Radio } from 'components';
import { H5 } from 'components/typography';
import { DatePickerButton, Input, TextArea } from 'design-system-web';
import {
  AssignedMemberDropdown,
  StaffTeamsMultiSelectDropdown,
} from 'pages/portal/admin/channel/service-requests/details/components';

import { ScheduleFormValidationErrors } from './types';
import { Assignable } from 'domains-work-order-type-contracts';

import { ScheduleEndDateType } from './constants';
import styles from './ScheduleDetails.scss';
import type { ClientPmSchedule } from 'graphql-query-contracts';
import { getTimeZoneByGeoLocation } from 'async-timing';
import { TaskExpirationSection } from './TaskExpirationSection';

interface ScheduleDetailsProps {
  channel: any;
  schedule: ClientPmSchedule | undefined;
  onScheduleFieldChange: (
    field: keyof ClientPmSchedule,
    value: string | boolean
  ) => void;
  validationErrors: ScheduleFormValidationErrors | undefined;
  assignedMember: Assignable | undefined;
  setAssignedMember: (member: Assignable | undefined) => void;
  assignedTeams: Assignable[];
  setAssignedTeams: (teams: Assignable[]) => void;
  endDateType: ScheduleEndDateType;
  setEndDateType: (type: ScheduleEndDateType) => void;
}

const REQUIRED_IDENTIFIER = '*';

export const ScheduleDetails = ({
  channel,
  schedule,
  onScheduleFieldChange,
  validationErrors,
  assignedMember,
  setAssignedMember,
  assignedTeams,
  setAssignedTeams,
  endDateType,
  setEndDateType,
}: ScheduleDetailsProps) => {
  const { t } = useTranslation();
  const translate = (key: string) => {
    return t(`web.admin.workOrder.preventiveMaintenance.schedule.Form.${key}`);
  };

  const [assignableMembers, _, assignableTeams] = useAssignableMembers(
    channel?._id
  );

  let channelTimeZone = DEFAULT_TZ;

  if (channel?.address?.geo) {
    const { geo } = channel.address;

    if (geo.length >= 2) {
      channelTimeZone = getTimeZoneByGeoLocation({
        longitude: geo[0],
        latitude: geo[1],
      });
    }
  }

  useEffect(() => {
    if (assignableMembers?.length > 0 && schedule?.assignee) {
      const assignedUser = assignableMembers.find(
        (u: { value: string }) =>
          convertToUUID(u.value) === schedule.assignee?.id
      );

      if (assignedUser) {
        setAssignedMember({
          label: assignedUser.label,
          displayName: assignedUser.displayName,
          value: assignedUser.value,
        });
      }
    }

    if (assignableTeams?.length > 0 && schedule?.assigneeGroups) {
      const assignedGroups = assignableTeams
        .filter(t =>
          schedule.assigneeGroups?.find(
            id => convertToUUID(id) === convertToUUID(t.value)
          )
        )
        .map(team => ({
          label: team.label,
          displayName: team.displayName,
          value: team.value,
        }));

      setAssignedTeams(assignedGroups);
    }
  }, [
    assignableMembers.length,
    assignableTeams.length,
    schedule?.assignee,
    setAssignedMember,
    setAssignedTeams,
  ]);

  const unAssignOption: Assignable = {
    label: t`web.admin.workOrder.shared.unassign`,
    value: 'unassign',
    displayName: t`web.admin.workOrder.shared.unassign`,
  };

  const assigneeOptions = [
    ...(assignedMember && assignableMembers?.length > 0
      ? [unAssignOption]
      : []),
    ...(assignableMembers ?? []),
  ];

  /**
   * Handles assignee change.
   *
   * @param newAssignee - The new assignee to set, or undefined to unassign
   */
  const handleAssigneeChanged = async (newAssignee: Assignable) => {
    if (newAssignee?.value === 'unassign') {
      setAssignedMember(undefined);
    } else {
      setAssignedMember(newAssignee);
    }
  };

  return (
    <Flex direction="column">
      {/* Schedule Title */}
      <Flex direction="column" className={styles.detailsSection}>
        <Label mt={0}>
          {translate('scheduleTitle')}
          <span className={styles.required}>{REQUIRED_IDENTIFIER}</span>
        </Label>
        <TextArea
          testId="schedule-title"
          value={schedule?.title ?? ''}
          onChange={(value: string) => {
            const isNonEmpty = value.trim().length > 0;

            if (isNonEmpty || value === '') {
              onScheduleFieldChange('title', value);
            }
          }}
          maxLength={50}
          showLengthIndicator={(schedule?.title || '').length > 0}
          errors={validationErrors?.title}
        />
      </Flex>

      {/* Schedule Assignees */}
      <Flex gap={3} className={styles.assigneeSection}>
        <StaffTeamsMultiSelectDropdown
          items={assignableTeams}
          isSearchable
          value={assignedTeams as any}
          onChange={(newTeams: any[]) => {
            setAssignedTeams(newTeams ?? []);
          }}
          placeholder={translate('select.teams')}
          label={translate('assign.teams')}
          className={styles.SelectDropdown}
          doTranslate={false}
        />

        <AssignedMemberDropdown
          fieldName="assign-to-person"
          users={assigneeOptions}
          setAssignedMember={handleAssigneeChanged}
          assignedMember={assignedMember}
          placeholder={translate('select.person')}
          label={translate('assign.person')}
          testId="assignedMemberDropdown"
          doTranslate={false}
        />
      </Flex>

      {/* Schedule Notes */}
      <Flex direction="column" className={styles.notesSection}>
        <Label mt={0}>{translate('notes')}</Label>
        <TextArea
          testId="schedule-notes"
          value={schedule?.notes ?? ''}
          onChange={(value: string) => onScheduleFieldChange('notes', value)}
          maxLength={3_000}
          showLengthIndicator
          minRows={4}
        />
      </Flex>

      {/* Schedule Estimates */}
      <Flex direction="column" className={styles.estimateSection}>
        <Label mt={0}>
          {translate('estimatedTimeToComplete')}
          <span className={styles.required}>{REQUIRED_IDENTIFIER}</span>
        </Label>
        <Flex direction="row">
          <Input
            className={styles.estimateInput}
            testId="schedule-estimate"
            value={schedule?.timeToComplete}
            onChange={(value: string) =>
              onScheduleFieldChange('timeToComplete', parseFloat(value) as any)
            }
            fieldName="time-to-complete"
            error={validationErrors?.timeToComplete}
            type="number"
            showClear
          />
          <div className={styles.helperText}>{translate('hours')}</div>
        </Flex>
      </Flex>

      {/* Schedule Recurrence */}
      <Flex direction="column" className={styles.recurrenceSection}>
        <Label mt={0}>
          {translate('repeats')}
          <span className={styles.required}>{REQUIRED_IDENTIFIER}</span>
        </Label>
        <Dropdown
          id="schedule-repeats"
          testId="schedule-repeats"
          value={schedule?.repeats}
          items={recurrenceOptions}
          onChange={({ value }: any) => onScheduleFieldChange('repeats', value)}
          name="schedule-repeats"
          className={styles.recurrenceDropdown}
        />
      </Flex>

      {/* Schedule Due Date */}
      <Flex direction="column" className={styles.dueDateSection}>
        <Label mt={0}>
          {translate('firstDueDate')}
          <span className={styles.required}>{REQUIRED_IDENTIFIER}</span>
        </Label>
        <DatePickerButton
          testId="schedule-due-date"
          className={styles.dueDatePicker}
          value={schedule?.nextDueDate}
          onChange={(d: Date) => {
            onScheduleFieldChange('nextDueDate', d as any);
          }}
          minDate={new Date()}
          dateError={validationErrors?.nextDueDate}
          placeholder={t(
            'web.admin.workOrder.preventiveMaintenance.schedule.Form.select.date'
          )}
          hideOnSelect
          hideLabel
          timeZone={channelTimeZone}
        />
      </Flex>

      {/* Schedule Advance */}
      <Flex gap={2} className={styles.advanceSection}>
        <Flex direction="column">
          <Label mt={0}>
            {translate('daysAhead')}
            <span className={styles.required}>{REQUIRED_IDENTIFIER}</span>
          </Label>
          <Input
            className={styles.advanceInput}
            testId="schedule-advance"
            value={schedule?.daysAhead}
            onChange={(value: string) =>
              onScheduleFieldChange(
                'daysAhead',
                value === '' ? '' : (Number(value) as any)
              )
            }
            type="number"
            maxExpand={false}
            error={validationErrors?.daysAhead}
            fieldName="days-ahead"
          />
        </Flex>
      </Flex>

      {/* Schedule Ends */}
      <Flex direction="column" gap={2} className={styles.endSection}>
        <H5>{translate('ends')}</H5>
        <Radio
          testId="schedule-ends-never"
          name="schedule-ends-never"
          key={ScheduleEndDateType.Never}
          selected={endDateType}
          onChange={() => {
            setEndDateType(ScheduleEndDateType.Never);
            onScheduleFieldChange('untilDate', undefined as any);
          }}
          value={ScheduleEndDateType.Never}
          text={translate('never')}
        />
        <Flex gap={2} align="center">
          <Radio
            testId="schedule-ends-specific"
            name="schedule-ends-specific"
            key={ScheduleEndDateType.Specific}
            selected={endDateType}
            onChange={() => setEndDateType(ScheduleEndDateType.Specific)}
            value={ScheduleEndDateType.Specific}
            text={translate('onSpecificDate')}
            doTranslate={false}
          />
          <DatePickerButton
            dateInputLabel={translate('endDate')}
            value={schedule?.untilDate}
            onChange={(d: Date) => {
              onScheduleFieldChange('untilDate', d as any);
            }}
            minDate={schedule?.nextDueDate!}
            disabled={endDateType !== ScheduleEndDateType.Specific}
            dateError={
              endDateType === ScheduleEndDateType.Specific &&
              validationErrors?.untilDate
                ? validationErrors.untilDate
                : undefined
            }
            hideOnSelect
            timeZone={channelTimeZone}
          />
        </Flex>
      </Flex>
      <TaskExpirationSection
        channelId={channel?._id}
        autoExpireTask={schedule?.autoExpireTask ?? false}
        onScheduleFieldChange={onScheduleFieldChange}
      />
    </Flex>
  );
};
